import { BaseDialog, Button, CustomerTable, Modal } from '@app/components';
import clsx from 'clsx';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useCreateCampaign } from './CreateCampaign.hooks';
import UsersIcon from '@app/assets/icons/customer-multiple.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import RemoveRedIcon from '@app/assets/icons/remove-red.svg?react';
import { useEffect, useState } from 'react';
import ArrowLeft from '@app/assets/icons/arrow-left.svg?react';
import ArrowRight from '@app/assets/icons/arrow-right.svg?react';
import CustomerBlueIcon from '@app/assets/icons/customer-blue.svg?react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';
import { CustomerInterface } from '@app/models/business';
import { ListsTable } from '@app/components/ListsTable/ListsTable';
import { ListInterface } from '@app/models/util';
import ChevronDown from '@app/assets/icons/chevron-down.svg?react';
import ChevronRight from '@app/assets/icons/chevron-right.svg?react';
import { CustomerCampaignFilters } from '@app/components/CampaignsFilters/CustomerCampaignFilters';
import FolderIcon from '@app/assets/icons/folder.svg?react';
import FolderDeleteIcon from '@app/assets/icons/folder-delete.svg?react';
import ImportFileIcon from '@app/assets/icons/import-file-general.svg?react';
import PlusIcon from '@app/assets/icons/plus-icon-general.svg?react';
import UserIcon from '@app/assets/icons/user.svg?react';
import { usePermissions } from '@app/contexts/PermissionContext.tsx';

export const CreateCampaignCustomers = ({
  form,
}: {
  form: UseFormReturn<FieldValues, any, undefined>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedLists, setSelectedLists] = useState<ListInterface[]>([]);
  const [isExpanded, setIsExpanded] = useState<string | null>(null);
  const [itemsSelectedForDeletion, setItemsSelectedForDeletion] = useState<{
    customers: string[];
    lists: string[];
  }>({ customers: [], lists: [] });

  const {
    customers,
    handleCustomersModal,
    showCustomerModal,
    saveFormData,
    handleListsModal,
    isLoadingLists,
    showListModal,
    listsData,
    handleListsLimit,
    handleListsPrevPage,
    handleListsNextPage,
    listQuery,
    refetch,
    handleFilterLists,
    handleCustomersNextPage,
    handleCustomersPrevPage,
    handleCustomersLimit,
    customerQuery,
    refetchCustomers,
    setCustomerFilters,
    listsAndTags,
    handleFilterCustomers,
    handleSortingTable,
    handleCustomerSortingTable,
    listSortBy,
    listSortOrder,
  } = useCreateCampaign(form);

  // Ensure the 'selectedCustomers' field exists in the form
  useEffect(() => {
    // Check if we have data in localStorage
    const storedFormData = localStorage.getItem('campaignFormData');
    if (storedFormData) {
      const parsedData = JSON.parse(storedFormData);
      // Update form with localStorage data if it exists
      Object.keys(parsedData).forEach((key) => {
        if (parsedData[key] !== undefined) {
          form.setValue(key, parsedData[key]);
        }
      });
    }

    setSelectedLists(
      JSON.parse(localStorage.getItem('campaignSelectedLists') || '[]')
    );
  }, [form]);

  const toggleCustomerSelection = (customerId: string) => {
    const currentSelected = form.getValues('selectedCustomers') || [];
    if (customerId === 'all') {
      const allCustomerIds =
        customers?.data?.customers.map(
          (customer: CustomerInterface) => customer.id
        ) || [];
      if (allCustomerIds.every((id: string) => currentSelected.includes(id))) {
        // Uncheck all if all are already selected
        form.setValue('selectedCustomers', [], { shouldDirty: true });
      } else {
        // Select all if not all are selected
        form.setValue('selectedCustomers', allCustomerIds, {
          shouldDirty: true,
        });
      }
    } else if (currentSelected.includes(customerId)) {
      form.setValue(
        'selectedCustomers',
        currentSelected.filter((id: string) => id !== customerId),
        { shouldDirty: true }
      );
    } else {
      form.setValue('selectedCustomers', [...currentSelected, customerId], {
        shouldDirty: true,
      });
    }

    // Save after selection changes
    saveFormData();
  };

  const toggleListSelection = (listId: string) => {
    const currentSelectedLists = form.getValues('selectedLists') || [];
    const allLists = listsData?.lists || [];
    const list = allLists.find((l) => l.id === listId);

    if (listId === 'all') {
      const allListIds = allLists.map((list) => list.id);
      const allCustomers = allLists.flatMap(
        (list) => list.customers?.map((customer) => customer.id) || []
      );

      const isAllSelected = allListIds.every((id) =>
        currentSelectedLists.includes(id)
      );

      setSelectedLists(isAllSelected ? [] : allLists);
      form.setValue('selectedLists', isAllSelected ? [] : allListIds, {
        shouldDirty: true,
      });
      form.setValue('selectedCustomers', isAllSelected ? [] : allCustomers, {
        shouldDirty: true,
      });
    } else if (list) {
      const isSelected = currentSelectedLists.includes(listId);
      const updatedLists = isSelected
        ? currentSelectedLists.filter((id: string) => id !== listId)
        : [...currentSelectedLists, listId];

      const currentCustomers = form.getValues('selectedCustomers') || [];
      const listCustomerIds = list.customers?.map((c) => c.id) || [];
      const updatedCustomers = isSelected
        ? currentCustomers
        : [...currentCustomers, ...listCustomerIds];

      setSelectedLists((prev) =>
        isSelected ? prev.filter((l) => l.id !== listId) : [...prev, list]
      );

      localStorage.setItem(
        'campaignSelectedLists',
        JSON.stringify(updatedLists)
      );
      form.setValue('selectedLists', updatedLists, { shouldDirty: true });
      form.setValue('selectedCustomers', updatedCustomers, {
        shouldDirty: true,
      });
    }

    saveFormData();
  };

  const toggleCustomerDeletionSelection = (customerId: string) => {
    setItemsSelectedForDeletion((prev) => ({
      ...prev,
      customers: prev.customers.includes(customerId)
        ? prev.customers.filter((id) => id !== customerId)
        : [...prev.customers, customerId],
    }));
  };

  const toggleListDeletionSelection = (listId: string) => {
    setItemsSelectedForDeletion((prev) => ({
      ...prev,
      lists: prev.lists.includes(listId)
        ? prev.lists.filter((id) => id !== listId)
        : [...prev.lists, listId],
    }));
  };

  const removeSelectedItems = () => {
    const { customers: customersToRemove, lists: listsToRemove } =
      itemsSelectedForDeletion;

    // Get all customer IDs from lists that are being deleted
    const customersFromDeletedLists = selectedLists
      .filter((list) => listsToRemove.includes(list.id))
      .flatMap((list) => list.customers?.map((customer) => customer.id) || []);

    // Combine explicitly selected customers with customers from deleted lists
    const allCustomersToRemove = [
      ...customersToRemove,
      ...customersFromDeletedLists,
    ];

    // Remove selected customers (including those from deleted lists)
    const currentSelectedCustomers = form.getValues('selectedCustomers') || [];
    const updatedCustomers = currentSelectedCustomers.filter(
      (id: string) => !allCustomersToRemove.includes(id)
    );

    // Remove customers from lists
    const updatedLists = selectedLists
      .map((list) => ({
        ...list,
        customers:
          list.customers?.filter(
            (customer: CustomerInterface) =>
              !customersToRemove.includes(customer.id)
          ) || [],
      }))
      .filter((list) => {
        // Remove lists that are selected for deletion or have no customers
        if (listsToRemove.includes(list.id)) return false;
        return list.customers && list.customers.length > 0;
      });

    // Update form values
    form.setValue('selectedCustomers', updatedCustomers, { shouldDirty: true });
    form.setValue(
      'selectedLists',
      updatedLists.map((list) => list.id),
      { shouldDirty: true }
    );

    // Update state
    setSelectedLists(updatedLists);

    // Clear deletion selections
    setItemsSelectedForDeletion({ customers: [], lists: [] });

    // Save to localStorage
    localStorage.setItem('campaignSelectedLists', JSON.stringify(updatedLists));
    saveFormData();
  };

  const deleteList = (listId: string) => {
    const currentSelectedLists = form.getValues('selectedLists') || [];
    const currentSelectedCustomers = form.getValues('selectedCustomers') || [];
    const list = selectedLists.find((l) => l.id === listId);

    if (list) {
      // Remove all customers from this list from selected customers
      const listCustomerIds =
        list.customers?.map((customer) => customer.id) || [];
      const updatedCustomers = currentSelectedCustomers.filter(
        (id: string) => !listCustomerIds.includes(id)
      );

      // Remove the list from selected lists
      const updatedLists = selectedLists.filter((l) => l.id !== listId);
      const updatedSelectedListIds = currentSelectedLists.filter(
        (id: string) => id !== listId
      );

      // Update form values
      form.setValue('selectedCustomers', updatedCustomers, {
        shouldDirty: true,
      });
      form.setValue('selectedLists', updatedSelectedListIds, {
        shouldDirty: true,
      });

      // Update state
      setSelectedLists(updatedLists);

      // Save to localStorage
      localStorage.setItem(
        'campaignSelectedLists',
        JSON.stringify(updatedLists)
      );
      saveFormData();
    }
  };

  const removeCustomerFromList = (listId: string, customerId: string) => {
    const currentSelectedLists = form.getValues('selectedLists') || [];
    const currentSelectedCustomers = form.getValues('selectedCustomers') || [];
    const list = selectedLists.find((l) => l.id === listId);

    if (list) {
      // Remove the customer from the list's customers
      const updatedListCustomers = list.customers?.filter(
        (customer) => customer.id !== customerId
      );

      // Update the selected lists state
      let updatedLists = selectedLists.map((l) =>
        l.id === listId ? { ...l, customers: updatedListCustomers } : l
      );

      // If the list has no more customers, remove it from selected lists
      if (updatedListCustomers?.length === 0) {
        updatedLists = updatedLists.filter((l) => l.id !== listId);
        form.setValue(
          'selectedLists',
          currentSelectedLists.filter((id: string) => id !== listId),
          { shouldDirty: true }
        );
      }

      // Check other lists for the same customerId
      updatedLists.forEach((otherList) => {
        if (
          otherList.id !== listId &&
          otherList.customers?.length === 1 &&
          otherList.customers[0].id === customerId
        ) {
          // Remove the other list if it only contains this customer
          updatedLists = updatedLists.filter((l) => l.id !== otherList.id);
          form.setValue(
            'selectedLists',
            currentSelectedLists.filter((id: string) => id !== otherList.id),
            { shouldDirty: true }
          );
        }
      });

      setSelectedLists(updatedLists);

      // Remove the customer from the selected customers
      const updatedCustomers = currentSelectedCustomers.filter(
        (id: string) => id !== customerId
      );

      form.setValue('selectedCustomers', updatedCustomers, {
        shouldDirty: true,
      });
      form.setValue(
        'selectedLists',
        updatedLists.map((list) => list.id),
        { shouldDirty: true }
      );

      // Save the updated lists and customers to localStorage
      localStorage.setItem(
        'campaignSelectedLists',
        JSON.stringify(updatedLists)
      );
      saveFormData();
    }
  };

  // Get the count of selected customers
  const getSelectedCustomersCount = () => {
    const selected = form.getValues('selectedCustomers') || [];
    return selected.length;
  };

  const getSelectedListsCount = () => {
    const selected = form.getValues('selectedLists') || [];
    return selected.length;
  };

  const getSelectedForDeletionCount = () => {
    return (
      itemsSelectedForDeletion.customers.length +
      itemsSelectedForDeletion.lists.length
    );
  };

  const handleNext = () => {
    // Save current form data before navigating
    const values = form.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));
    navigate('/campaigns/create/campaign');
  };

  const handleBack = () => {
    // Save current form data before navigating
    const values = form.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('campaignFormData', JSON.stringify(values));
    navigate('/campaigns/create/settings');
  };

  const handleConfirmListSelection = () => {
    if (selectedLists.length > 0)
      localStorage.setItem(
        'campaignSelectedLists',
        JSON.stringify(selectedLists)
      );

    handleListsModal();
  };
  const { can } = usePermissions();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Main content area with proper scrolling */}
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto p-2 md:p-4 flex flex-col justify-between items-center gap-4">
          <div className="bg-white w-full max-w-[800px] lg:max-w-[1000px] p-2 md:p-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold">Send to customers</h4>
              <p className="text-xs text-base-coolGray">
                Filter the customers to whom you want to send this request. If
                nothing is selected, the request will be sent to all{' '}
              </p>
            </div>
            <hr />
            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              <Button
                kind="secondary"
                className="min-w-fit"
                onClick={() => handleCustomersModal()}
              >
                Select customers
              </Button>
              <Button
                kind="secondary"
                className="min-w-fit"
                onClick={() => handleListsModal()}
                leftNode={<FolderIcon />}
              >
                Select lists
              </Button>
              {can('addCustomers') && (
                <>
                  <Button
                    kind="secondary"
                    className="min-w-fit"
                    onClick={() => navigate('/customers/contacts/import-file')}
                    leftNode={<ImportFileIcon />}
                  >
                    Import from file
                  </Button>
                  <Button
                    kind="secondary"
                    className="min-w-fit"
                    onClick={() => navigate('/customers/contacts?create=true')}
                    leftNode={<PlusIcon />}
                  >
                    Create customer
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h4 className="font-bold mt-4 text-sm sm:text-base">
                {getSelectedCustomersCount()} customers selected
              </h4>
              {getSelectedForDeletionCount() > 0 && (
                <button
                  type="button"
                  onClick={removeSelectedItems}
                  className="flex items-center gap-1 text-red-500 text-xs sm:text-sm"
                >
                  <RemoveRedIcon width={14} height={14} />
                  Remove {getSelectedForDeletionCount()} selected
                </button>
              )}
            </div>
            <hr className="border-t border-gray-200 my-4" />
            {/* Customer list with proper scrolling */}
            <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
              {customers?.data?.customers &&
              form.getValues('selectedCustomers')?.length > 0 ? (
                <div className="space-y-2">
                  {customers.data.customers
                    .filter(
                      (customer: CustomerInterface) =>
                        form
                          .getValues('selectedCustomers')
                          ?.includes(customer.id) &&
                        !selectedLists.some((list) =>
                          list.customers?.some(
                            (listCustomer) => listCustomer.id === customer.id
                          )
                        )
                    )
                    .map((customer: CustomerInterface) => (
                      <div
                        key={customer.id}
                        className={clsx(
                          'border-b border-b-base-ghostWhite p-2 sm:p-3 rounded flex justify-between items-center group cursor-pointer',
                          {
                            'bg-blue-50':
                              itemsSelectedForDeletion.customers.includes(
                                customer.id
                              ),
                          }
                        )}
                      >
                        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                          <input
                            type="checkbox"
                            checked={itemsSelectedForDeletion.customers.includes(
                              customer.id
                            )}
                            onChange={() =>
                              toggleCustomerDeletionSelection(customer.id)
                            }
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="bg-[#F6F7FA] rounded-md p-1 flex-shrink-0">
                            <UserIcon width={16} height={16} />
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1">
                            <p className="font-medium text-sm sm:text-base truncate">
                              {customer.firstName} {customer.lastName}
                            </p>
                            <p className="text-xs sm:text-sm text-base-coolGray truncate">
                              {customer.email}
                            </p>
                          </div>
                        </div>
                        {!itemsSelectedForDeletion.customers.includes(
                          customer.id
                        ) && (
                          <button
                            type="button"
                            onClick={() => toggleCustomerSelection(customer.id)}
                            className="bg-red-50 hover:bg-red-100 p-1 rounded transition-colors flex items-center justify-center self-center opacity-0 group-hover:opacity-100 flex-shrink-0"
                          >
                            <CloseIcon
                              width={16}
                              height={16}
                              className="text-red-500"
                            />
                          </button>
                        )}
                      </div>
                    ))}
                  {selectedLists.map((list) => {
                    const isListExpanded = isExpanded === list.id;
                    return (
                      <div
                        key={list.id}
                        className="border-b border-b-base-ghostWhite p-3 rounded"
                      >
                        <div
                          className={clsx(
                            'flex justify-between items-center p-2 sm:p-3 rounded group cursor-pointer',
                            {
                              'bg-blue-50':
                                itemsSelectedForDeletion.lists.includes(
                                  list.id
                                ),
                            }
                          )}
                        >
                          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                            <input
                              type="checkbox"
                              checked={itemsSelectedForDeletion.lists.includes(
                                list.id
                              )}
                              onChange={() =>
                                toggleListDeletionSelection(list.id)
                              }
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setIsExpanded(isListExpanded ? null : list.id)
                              }
                              className="text-base-coolGray hover:text-base-night"
                            >
                              {isListExpanded ? (
                                <ChevronDown />
                              ) : (
                                <ChevronRight />
                              )}
                            </button>
                            <div className="bg-[#F6F7FA] rounded-md p-1 sm:p-2 flex-shrink-0">
                              <FolderIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <div className="font-bold text-base-night text-sm sm:text-base truncate">
                              {list.name} ({list.customers?.length || 0})
                            </div>
                          </div>
                          {!itemsSelectedForDeletion.lists.includes(
                            list.id
                          ) && (
                            <button
                              type="button"
                              onClick={() => deleteList(list.id)}
                              className="bg-red-50 hover:bg-red-100 p-1 rounded transition-colors flex items-center justify-center self-center opacity-0 group-hover:opacity-100 flex-shrink-0"
                              title="Delete list and all its customers"
                            >
                              <FolderDeleteIcon
                                width={16}
                                height={16}
                                className="text-red-500"
                              />
                            </button>
                          )}
                        </div>
                        {isListExpanded && (
                          <div className="pl-2 sm:pl-4">
                            {(list.customers || [])
                              .filter((customer: CustomerInterface) =>
                                form
                                  .getValues('selectedCustomers')
                                  ?.includes(customer.id)
                              )
                              .map((customer: CustomerInterface) => (
                                <div
                                  key={customer.id}
                                  className={clsx(
                                    'border-b border-b-base-ghostWhite p-2 sm:p-3 rounded flex justify-between items-center group cursor-pointer',
                                    {
                                      'bg-blue-50':
                                        itemsSelectedForDeletion.customers.includes(
                                          customer.id
                                        ),
                                    }
                                  )}
                                >
                                  <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={itemsSelectedForDeletion.customers.includes(
                                        customer.id
                                      )}
                                      onChange={() =>
                                        toggleCustomerDeletionSelection(
                                          customer.id
                                        )
                                      }
                                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <div className="bg-[#F6F7FA] rounded-md p-1 flex-shrink-0">
                                      <UserIcon width={16} height={16} />
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1">
                                      <p className="font-medium text-sm sm:text-base truncate">
                                        {customer.firstName} {customer.lastName}
                                      </p>
                                      <p className="text-xs sm:text-sm text-base-coolGray truncate">
                                        {customer.email}
                                      </p>
                                    </div>
                                  </div>
                                  {!itemsSelectedForDeletion.customers.includes(
                                    customer.id
                                  ) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        toggleCustomerSelection(customer.id);
                                        removeCustomerFromList(
                                          list.id,
                                          customer.id
                                        );
                                      }}
                                      className="bg-red-50 hover:bg-red-100 p-1 rounded transition-colors flex items-center justify-center self-center opacity-0 group-hover:opacity-100 flex-shrink-0"
                                    >
                                      <CloseIcon
                                        width={16}
                                        height={16}
                                        className="text-red-500"
                                      />
                                    </button>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed bottom navigation - positioned relative to content area */}
      <div className="absolute bottom-0 w-full bg-white border-t border-gray-200 p-2 md:p-4 mt-auto z-10">
        <div className="container mx-auto px-2 md:px-4 flex items-center justify-between">
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
            <Button
              kind="secondary"
              type="button"
              onClick={handleBack}
              className="bg-secondary border min-w-fit"
            >
              <ArrowLeft />
            </Button>

            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-gray-700 text-center">
              <span className="text-xs sm:text-sm">
                Your campaign will be sent to
              </span>
              <div className="flex items-center gap-1 bg-[#F8FCFF] rounded-md py-1 sm:py-2 px-2 sm:px-3">
                <CustomerBlueIcon
                  width={14}
                  height={14}
                  className="sm:w-4 sm:h-4"
                />
                <span className="font-medium text-xs sm:text-sm lg:text-base">
                  {getSelectedCustomersCount()} Customers
                </span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleNext}
              className="bg-primary min-w-fit"
            >
              <ArrowRight className="[&_path]:stroke-[white]" />
            </Button>
          </div>
        </div>
      </div>

      <Modal
        size="max-w-6xl"
        open={showCustomerModal}
        onClose={handleCustomersModal}
      >
        <div className="p-4 sm:p-6 mb-6 flex flex-col sm:flex-row gap-4 sm:gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <UsersIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Select customers</h5>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-80 lg:w-96">
              <input
                name="searchCustomers"
                placeholder="Search"
                className="w-full border-0 border-b border-gray-300 py-3 px-4 focus:outline-none focus:border-gray-400 bg-transparent placeholder:text-gray-400"
                onChange={(e) => {
                  const searchValue = e.target.value;
                  // If the field is cleared, reset the search
                  if (searchValue === '') {
                    handleFilterCustomers('');
                  } else if (searchValue.length >= 3) {
                    // Trigger search after 3 or more characters
                    handleFilterCustomers(searchValue);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const searchValue = e.currentTarget.value;
                    handleFilterCustomers(searchValue);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const searchValue =
                    (
                      document.querySelector(
                        'input[name="searchCustomers"]'
                      ) as HTMLInputElement
                    )?.value || '';
                  handleFilterCustomers(searchValue);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                  <circle
                    cx="11"
                    cy="11"
                    r="8"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    d="M21 21l-3.5-3.5"
                  />
                </svg>
              </button>
            </div>
            <button type="button" onClick={() => handleCustomersModal()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-6 pb-4 max-h-[500px] overflow-y-auto">
          <div className="container mx-auto px-2 sm:px-4 py-2">
            <CustomerCampaignFilters
              setCustomerFilters={setCustomerFilters}
              lists={listsAndTags?.lists}
              tags={listsAndTags?.tags}
            />
          </div>
          <hr className="border-t border-gray-200 my-4" />

          <CustomerTable
            simplified
            currentPage={customerQuery.page}
            customers={customers?.data?.customers ?? []}
            totalCustomers={customers?.data?.count ?? 0}
            externalForm={form}
            handleNextPage={handleCustomersNextPage}
            handlePrevPage={handleCustomersPrevPage}
            handleLimitChange={handleCustomersLimit}
            handleSortingTable={handleCustomerSortingTable}
            handleCustomerSelection={(customerId?: string) => {
              if (customerId) {
                toggleCustomerSelection(customerId);
              }
            }}
            refetch={refetchCustomers}
          />
        </div>
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 justify-center">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={() => handleCustomersModal()}
            >
              Cancel
            </Button>
            <Button
              kind="primary"
              className="w-full"
              type="button"
              onClick={handleCustomersModal}
            >
              Apply {getSelectedCustomersCount()} selected customer(s)
            </Button>
          </div>
        </div>
      </Modal>

      <BaseDialog
        size="max-w-6xl"
        open={showListModal}
        onClose={handleListsModal}
      >
        <div className="border-b border-b-base-ghostWhite p-4 sm:p-6 mb-6 flex flex-col sm:flex-row gap-4 sm:gap-2 items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
              <FolderIcon width={24} height={24} />
            </div>
            <h5 className="font-bold text-lg">Select customer lists</h5>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
            {!isLoadingLists && (
              <div className="relative w-full sm:w-80 lg:w-96">
                <input
                  name="searchLists"
                  placeholder="Search"
                  className="w-full border-0 border-b border-gray-300 py-3 px-4 focus:outline-none focus:border-gray-400 bg-transparent placeholder:text-gray-400"
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    // If the field is cleared, reset the search
                    if (searchValue === '') {
                      handleFilterLists('');
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const searchValue =
                      (
                        document.querySelector(
                          'input[name="searchLists"]'
                        ) as HTMLInputElement
                      )?.value || '';
                    handleFilterLists(searchValue);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      d="M21 21l-3.5-3.5"
                    />
                  </svg>
                </button>
              </div>
            )}
            <button type="button" onClick={() => handleListsModal()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-6 pb-4 max-h-[500px] overflow-y-auto">
          <ListsTable
            lists={(listsData?.lists as ListInterface[]) ?? []}
            simplified={true}
            handleLimit={handleListsLimit}
            handleNextPage={handleListsNextPage}
            handlePrevPage={handleListsPrevPage}
            page={listQuery.page}
            total={listsData?.count ?? 0}
            refresh={refetch}
            handleListSelect={(listId?: string) => {
              if (listId) toggleListSelection(listId);
            }}
            handleAddCustomersToList={function (): void {
              throw new Error('Function not implemented.');
            }}
            selectedLists={form.getValues('selectedLists') ?? []}
            sortable={true}
            currentSortBy={listSortBy}
            currentSortOrder={listSortOrder}
            onSort={handleSortingTable}
          />
        </div>
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 justify-center">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={() => handleListsModal()}
            >
              Cancel
            </Button>
            <Button
              kind="primary"
              className="w-full"
              type="button"
              onClick={handleConfirmListSelection}
            >
              Apply {getSelectedListsCount()} selected list(s)
            </Button>
          </div>
        </div>
      </BaseDialog>
    </div>
  );
};
